import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Card, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import CrudService from "services/cruds-service";
import { toast } from "react-toastify";
import AlarmTank from "./AlarmTank";
import {
  DISTANCE_FACTORS, INTERVAL_FACTORS, MM_PER_INCH, alarmAmountsFromDistances, alarmPreview,
  alarmWarnings, buildValues, calibrationFingerprint, calibrationGeometry,
  commandKind, commandTitle, initialValues, normalizeCatalog, numeric,
} from "./helpers";
import { color } from "framer-motion";

function errorMessage(error) {
  return error?.message || error?.error || "The request failed. Please try again.";
}

async function readCalibration(deviceId) {
  try {
    const response = await CrudService.getLevelDeviceConfiguration(deviceId);
    if (response?.code === "LEVEL_DEVICE_CONFIGURATION_NOT_FOUND") return null;
    if (response?.success === false || !response?.data ||
        typeof response.data !== "object" || Array.isArray(response.data)) {
      throw new Error(response?.message || "Invalid calibration lookup response.");
    }
    return response.data;
  } catch (error) {
    if (error?.code === "LEVEL_DEVICE_CONFIGURATION_NOT_FOUND") return null;
    throw error;
  }
}

const distanceUnits = { in: "Inches", cm: "Centimeters", mm: "Millimeters" };
const intervalUnits = { seconds: "Seconds", minutes: "Minutes", hours: "Hours" };
const defaultOptions = { mode: "gap", unit: "in", intervalUnit: "hours" };

export default function SensorSettings({
  deviceId, modelId, deviceName, canWrite, dimensions, reading,
}) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const textColor = controller.darkMode ? "white" : "dark";
  const identity = `${deviceId}:${modelId}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const alive = useRef(true);
  const sendingRef = useRef(false);
  const permissionRef = useRef(canWrite);
  permissionRef.current = canWrite;
  const [load, setLoad] = useState({ key: "", busy: true, commands: [], error: "" });
  const [cal, setCal] = useState({ key: "", busy: true, data: null, error: "" });
  const [refresh, setRefresh] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState({});
  const [options, setOptions] = useState(defaultOptions);
  const [formError, setFormError] = useState("");
  const [review, setReview] = useState(null);
  const [ack, setAck] = useState(false);
  const [sending, setSending] = useState(false);
  const [requests, setRequests] = useState([]);
  const [outcome, setOutcome] = useState("");

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setSelectedId("");
    setDraft({});
    setReview(null);
    setAck(false);
    setFormError("");
    setOutcome("");
    setRequests([]);
    setLoad({ key: identity, busy: true, commands: [], error: "" });
    setCal({ key: identity, busy: true, data: null, error: "" });
    const valid = value => Number.isInteger(numeric(value)) && numeric(value) > 0;
    if (!valid(deviceId) || !valid(modelId)) {
      setLoad({ key: identity, busy: false, commands: [], error: "Device model is not available." });
      setCal({ key: identity, busy: false, data: null, error: "Device identity is not available." });
      return () => { active = false; };
    }
    (async () => {
      try {
        const response = await CrudService.getCommandsByModelId(modelId);
        if (!response?.success) throw new Error(response?.message || "Could not load sensor settings.");
        const commands = normalizeCatalog(response.data);
        if (active) setLoad({ key: identity, busy: false, commands, error: "" });
      } catch (error) {
        console.error("Sensor settings catalog lookup failed", error);
        if (active) setLoad({ key: identity, busy: false, commands: [], error: errorMessage(error) });
      }
    })();
    (async () => {
      try {
        const data = await readCalibration(deviceId);
        if (active) setCal({ key: identity, busy: false, data, error: "" });
      } catch (error) {
        console.error("Sensor calibration lookup failed", error);
        if (active) setCal({ key: identity, busy: false, data: null, error: errorMessage(error) });
      }
    })();
    return () => { active = false; };
  }, [deviceId, modelId, identity, refresh]);

  useEffect(() => {
    if (!canWrite) {
      setReview(null);
      setAck(false);
    }
  }, [canWrite]);

  const commands = load.key === identity ? load.commands : [];
  const selected = commands.find(command => String(command.id) === selectedId);
  const kind = commandKind(selected);
  const calibrationReady = cal.key === identity && !cal.busy && !cal.error;
  const geometry = calibrationReady ? calibrationGeometry(cal.data) : null;
  const primary = commands.filter(command => commandKind(command) !== "generic");
  const secondary = commands.filter(command => commandKind(command) === "generic");
  const busy = sending || load.busy || load.key !== identity;
  const surfaces = {
    p: { xs: 2, md: 3 }, minWidth: 0,
    backgroundColor: theme.palette.background.card,
    border: `1px solid ${theme.functions.rgba(theme.palette.text.main, 0.12)}`,
  };

  function choose(command) {
    const nextKind = commandKind(command);
    setSelectedId(String(command.id));
    setReview(null);
    setAck(false);
    setFormError("");
    if (nextKind === "alarms") {
      setOptions({ ...defaultOptions, mode: geometry ? "percent" : "gap" });
      setDraft({ min1: geometry ? "80" : "", max1: "" });
    } else if (nextKind === "interval") {
      setOptions(defaultOptions);
      const seconds = numeric(command.fields[0].default);
      setDraft({ value: seconds === null ? "" : String(seconds / 3600) });
    } else {
      setOptions(defaultOptions);
      setDraft(initialValues(command));
    }
  }

  let values = null;
  let validation = "";
  if (selected) {
    try {
      if (kind === "alarms" && !calibrationReady) {
        throw new Error(cal.error || "Wait for the calibration lookup.");
      }
      console.info('commands:', commands);
      console.info('Building values for command:', selected, draft, options, cal.data);
      values = buildValues(selected, draft, options, cal.data);
    } catch (error) {
      console.error("Error building values:", error);
      validation = errorMessage(error);
    }
  }
  const warnings = kind === "alarms" && values ? alarmWarnings(values, geometry, dimensions) : [];
  const diagramValues = kind === "alarms" && calibrationReady
    ? alarmPreview(draft, options, cal.data).values : null;
  const requestPayload = values && selected ? {
    command_id: selected.id, values, device_id: numeric(deviceId),
  } : null;

  function changeOption(key, value) {
    try {
      if (kind === "alarms") {
        const next = { ...options, [key]: value };
        const wire = {};
        for (const name of ["min1", "max1"]) {
          wire[name] = String(draft[name] ?? "").trim() === "" ? null :
            buildValues(selected, { min1: draft[name], max1: draft[name] }, options, cal.data).min1;
        }
        setDraft(alarmAmountsFromDistances(wire, next.mode, next.unit, geometry));
        setOptions(next);
      } else {
        const seconds = numeric(draft.value);
        setDraft({ value: seconds === null ? "" :
          String(seconds * INTERVAL_FACTORS[options.intervalUnit] / INTERVAL_FACTORS[value]) });
        setOptions({ ...options, intervalUnit: value });
      }
      setFormError("");
    } catch (error) {
      setFormError(`Correct the entered value before changing units: ${errorMessage(error)}`);
    }
  }

  function presetHigh() {
    if (!geometry) return;
    const amount = options.mode === "percent" ? 80 :
      options.mode === "height" ? geometry.full * 0.8 * MM_PER_INCH / DISTANCE_FACTORS[options.unit] :
        (geometry.sensor - geometry.full * 0.8) * MM_PER_INCH / DISTANCE_FACTORS[options.unit];
    setDraft(previous => ({ ...previous, min1: String(amount) }));
    setFormError("");
  }

  function startReview(event) {
    event.preventDefault();
    if (!selected || !requestPayload || !canWrite || busy) {
      console.log('validation', validation);
      setFormError(validation || "This request cannot be sent.");
      return;
    }
    setFormError("");
    setAck(false);
    setReview({
      identity, name: deviceName, title: commandTitle(selected), kind,
      payload: requestPayload,
      calibration: calibrationFingerprint(cal.data),
      rows: describeValues(selected, values, geometry),
      warnings: [...warnings],
    });
  }

  async function confirmSend() {
    if (!review || !ack || sendingRef.current || !permissionRef.current ||
        review.identity !== identityRef.current) return;
    const frozen = review;
    sendingRef.current = true;
    setSending(true);
    let submitted = false;
    try {
      if (frozen.kind === "alarms") {
        const fresh = await readCalibration(deviceId);
        if (!alive.current || identityRef.current !== frozen.identity || !permissionRef.current) return;
        if (calibrationFingerprint(fresh) !== frozen.calibration) {
          setCal({ key: identity, busy: false, data: fresh, error: "" });
          setReview(null);
          setDraft({ min1: "", max1: "" });
          setOptions({ ...defaultOptions, mode: calibrationGeometry(fresh) ? "percent" : "gap" });
          setFormError("Saved calibration changed. Re-enter and review both alarms.");
          return;
        }
      }
      if (!alive.current || identityRef.current !== frozen.identity || !permissionRef.current) return;
      submitted = true;
      const response = await CrudService.sendCommand(frozen.payload);
      if (!response?.success) throw new Error(response?.message || "The request was not accepted.");
      if (!alive.current || identityRef.current !== frozen.identity) {
        toast.info(`Request accepted for ${frozen.name}. Device application is not confirmed.`);
        return;
      }
      setRequests(previous => [{
        title: frozen.title, rows: frozen.rows, payload: frozen.payload,
        created: new Date().toLocaleString(),
      }, ...previous].slice(0, 10));
      setOutcome(`Request accepted for ${frozen.name}. Waiting for device confirmation.`);
      setReview(null);
      setAck(false);
      toast.success("Request accepted. Sensor application is not confirmed.");
    } catch (error) {
      console.error("Sensor setting request failed", error);
      const message = submitted
        ? `${errorMessage(error)} Delivery is not confirmed. Check the queue before retrying to avoid a duplicate.`
        : `Calibration could not be checked. Nothing was sent. ${errorMessage(error)}`;
      if (alive.current && identityRef.current === frozen.identity) {
        setFormError(message);
        setReview(null);
      }
      toast.error(message);
    } finally {
      sendingRef.current = false;
      if (alive.current) setSending(false);
    }
  }

  function update(key, value) {
    setDraft(previous => ({ ...previous, [key]: value }));
    setFormError("");
  }

  return (
    <Card id="level-sensor-settings" sx={surfaces}>
      <MDTypography variant="h5" color={textColor}>Sensor settings</MDTypography>
      <MDTypography variant="caption">Choose a setting. Review before sending.</MDTypography>
      {(load.busy || load.key !== identity) && <MDTypography variant="body2">Loading settings...</MDTypography>}
      {load.error && <Alert severity="error" sx={{ mt: 2 }}>{load.error}</Alert>}
      {(load.error || cal.error) && <MDButton color="info" size="small" disabled={sending} onClick={() => setRefresh(value => value + 1)}>Retry lookup</MDButton>}
      {!load.busy && !load.error && !commands.length && <Alert severity="info" sx={{ mt: 2 }}>No remote settings are available for this model.</Alert>}
      {!canWrite && <Alert severity="info" sx={{ mt: 2 }}>View only. Sending requires device write access.</Alert>}
      {outcome && <Alert severity="info" sx={{ mt: 2 }}>{outcome}</Alert>}
      <MDBox display="flex" gap={1} flexWrap="wrap" mt={2}>
        {[...primary].sort((a, b) => Number(commandKind(a) === "interval") - Number(commandKind(b) === "interval")).map(command => (
          <MDButton key={command.id} color="info"
            variant={selected?.id === command.id ? "contained" : "outlined"}
            disabled={busy} aria-pressed={selected?.id === command.id} onClick={() => choose(command)}>
            {commandTitle(command)}
          </MDButton>
        ))}
      </MDBox>
      {secondary.length > 0 && (
        <TextField select fullWidth label="Other sensor settings" sx={{ mt: 2, height: '3rem !important', '& .MuiOutlinedInput-root': { height: '3rem !important' } }}
          value={secondary.some(command => String(command.id) === selectedId) ? selectedId : ""}
          disabled={busy} onChange={event => choose(secondary.find(command => String(command.id) === event.target.value))} >
          <MenuItem value="" disabled >Choose a setting</MenuItem>
          {secondary.map(command => <MenuItem key={command.id} value={String(command.id)} >{command.command_name} / {command.command_type}</MenuItem>)}
        </TextField>
      )}
      {selected && (
        <MDBox component="form" onSubmit={startReview} mt={3}>
          <MDTypography variant="h6" color={textColor}>{commandTitle(selected)}</MDTypography>
          {kind === "alarms" && (
            <>
              <MDTypography variant="caption">Choose liquid level; we'll calculate the sensor gap. Alarms check each sample, not continuously.</MDTypography>
              {cal.busy && <Alert severity="info">Looking up saved calibration...</Alert>}
              {cal.error && <Alert severity="error">Calibration lookup failed: {cal.error}</Alert>}
              {calibrationReady && !geometry && <Alert severity="warning" sx={{ mt: 1 }}>No usable top-mounted calibration. Enter air gaps directly; tank size is guidance only.</Alert>}
              <AlarmTank geometry={geometry} dimensions={dimensions} reading={reading} values={diagramValues} />
              <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={2}>
                <TextField select label="Set alarms using" value={options.mode} disabled={busy}
                  onChange={event => changeOption("mode", event.target.value)} sx={{ mt: 2, height: '3rem !important', '& .MuiOutlinedInput-root': { height: '3rem !important' } }}>
                  <MenuItem value="percent" disabled={!geometry}>Percent full</MenuItem>
                  <MenuItem value="height" disabled={!geometry}>Liquid height from floor</MenuItem>
                  <MenuItem value="gap">Air gap from sensor</MenuItem>
                </TextField>
                {options.mode !== "percent" && <TextField select label="Your measuring unit" value={options.unit} disabled={busy}
                  onChange={event => changeOption("unit", event.target.value)} sx={{ mt: 2, height: '3rem !important', '& .MuiOutlinedInput-root': { height: '3rem !important' } }}>
                  {Object.entries(distanceUnits).map(([value, label]) => <MenuItem key={value} value={label}>{label}</MenuItem>)}
                </TextField>}
                {console.log('theme.palette.mode', theme.palette.mode)}
                {["min1", "max1"].map(key => <TextField key={key} type="number" required
                  label={key === "min1" ? "Tank getting full" : "Tank getting empty"}
                  value={draft[key] ?? ""} disabled={busy}
                  onChange={event => update(key, event.target.value)}
                  inputProps={{ min: 0, max: options.mode === "percent" ? 100 : undefined, step: "any" }}
                  sx={{ '& .MuiFormHelperText-root': { color: `${controller.darkMode ? '#fff' : '#000'} !important` } }}
                  helperText={`${key === "min1" ? options.mode === "gap" ? "Alarm below this gap" : "Alarm above this level" :
                    options.mode === "gap" ? "Alarm above this gap" : "Alarm below this level"} / ${options.mode === "percent" ? "% full" : distanceUnits[options.unit]}`} />)}
              </MDBox>
              {geometry && <MDBox display="flex" gap={1} alignItems="center" mt={1}>
                <MDButton size="small" color="info" disabled={busy} onClick={presetHigh}>80% full example</MDButton>
                <MDTypography variant="caption">Editable example, not current settings.</MDTypography>
              </MDBox>}
              <MDBox component="details" mt={2}>
                <MDBox component="summary" sx={{ cursor: "pointer", color: `${controller.darkMode ? '#fff' : '#000'} !important`, fontWeight: "bold", fontSize: "0.875rem" }}>Saved measurements (read-only)</MDBox>
                <MDTypography variant="caption" component="div" sx={{ mt: 1, display: "block", opacity: 0.8 }}>
                  {calibrationReady ? geometry ? `Sensor: ${geometry.sensor} in above floor. Full point: ${geometry.full} in.` :
                    cal.data ? "Saved calibration cannot support this conversion." : "No saved calibration." : "Lookup not available."}
                  {" "}Depth: {numeric(dimensions?.depth) ?? "unknown"} in;
                  width: {numeric(dimensions?.width) ?? "unknown"} in;
                  length: {numeric(dimensions?.length) ?? "unknown"} in.
                </MDTypography>
              </MDBox>
            </>
          )}
          {kind === "interval" && (
            <>
              <MDTypography variant="caption">More frequent readings can use more battery and data.</MDTypography>
              <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={2}>
                <TextField type="number" required label="Send a reading every" value={draft.value ?? ""}
                  disabled={busy} onChange={event => update("value", event.target.value)}
                  inputProps={{ step: "any", min: 60 / INTERVAL_FACTORS[options.intervalUnit], max: 86400 / INTERVAL_FACTORS[options.intervalUnit] }}
                  helperText="Allowed: 1 minute to 24 hours. Catalog default, not confirmed current setting." />
                <TextField select label="Time unit" value={options.intervalUnit} disabled={busy}
                  onChange={event => changeOption("intervalUnit", event.target.value)} sx={{ mt: 2, height: '3rem !important', '& .MuiOutlinedInput-root': { height: '3rem !important' } }}>
                  {Object.entries(intervalUnits).map(([value, label]) => <MenuItem key={value} value={label}>{label}</MenuItem>)}
                </TextField>
              </MDBox>
              <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                {[15, 30, 60, 120].map(minutes => <MDButton key={minutes} size="small" color="info" disabled={busy}
                  onClick={() => { setOptions(previous => ({ ...previous, intervalUnit: "minutes" })); update("value", String(minutes)); }}>
                  {minutes < 60 ? `${minutes} min` : `${minutes / 60} hour${minutes > 60 ? "s" : ""}`}
                </MDButton>)}
              </MDBox>
              {values && <MDTypography variant="caption" display="block" mt={1}>Will send every {values.value} seconds.</MDTypography>}
            </>
          )}
          {kind === "generic" && (
            <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={2}>
              {selected.problem ? <Alert severity="error">{selected.problem}</Alert> :
                selected.fields.length === 0 ? <Alert severity="warning">No inputs needed. Review this action carefully before sending.</Alert> :
                  selected.fields.map(field => <TextField key={field.key} select={field.type === "select"}
                    type={field.type === "number" ? "number" : "text"} fullWidth
                    label={field.label} value={draft[field.key] ?? ""} disabled={busy}
                    required={field.required !== false} onChange={event => update(field.key, event.target.value)}
                    inputProps={{ min: field.min, max: field.max, maxLength: field.maxLength, step: field.hex_bytes ? 1 : "any" }}>
                    {field.type === "select" ? field.options.map(option => <MenuItem key={String(option.value)} value={String(option.value)}>{option.label}</MenuItem>) : null}
                  </TextField>)}
            </MDBox>
          )}
          {warnings.map((warning, index) => <Alert severity="warning" key={index} sx={{ mt: 1 }}>{warning}</Alert>)}
          {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
          {!formError && validation && <MDTypography variant="caption" display="block" mt={2}>{validation}</MDTypography>}
          <MDBox component="details" mt={2}>
            <MDBox component="summary" sx={{ cursor: "pointer", color: `${controller.darkMode ? '#fff' : '#000'} !important`, fontWeight: "bold", fontSize: "0.875rem" }}>Technical details (optional)</MDBox>
            <MDTypography variant="caption" component="div" sx={{ mt: 1, display: "block", opacity: 0.8, color: `${controller.darkMode ? '#fff' : '#000'} !important` }}>{selected.description}</MDTypography>
            <MDBox component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12, mt: 1, color: `${controller.darkMode ? '#fff' : '#000'} !important` }}>
              {requestPayload ? JSON.stringify(requestPayload, null, 2) : "Complete valid values to preview the request."}
            </MDBox>
          </MDBox>
          <MDButton type="submit" color="info" variant="contained" sx={{ mt: 2 }}
            disabled={!canWrite || busy || !!validation || !requestPayload}>Review {selected.fields.length ? "change" : "action"}</MDButton>
        </MDBox>
      )}
      {requests.length > 0 && <MDBox mt={3}>
        <MDTypography variant="h6" color={textColor}>Requests sent this visit</MDTypography>
        {requests.map((request, index) => <MDBox key={`${request.created}:${index}`} mt={1} p={1}
          sx={{ border: `1px solid ${theme.functions.rgba(theme.palette.text.main, 0.12)}`, borderRadius: 1 }}>
          <MDTypography variant="body2">{request.title}</MDTypography>
          <MDTypography variant="caption">Accepted / application not confirmed / {request.created}</MDTypography>
          <MDBox component="details">
            <MDBox component="summary" sx={{ cursor: "pointer" }}>View request</MDBox>
            {request.rows.map(row => <MDTypography key={row.label} variant="caption" display="block">{row.label}: {row.value}</MDTypography>)}
          </MDBox>
        </MDBox>)}
      </MDBox>}
      <Dialog open={!!review} onClose={() => { if (!sending) setReview(null); }} fullWidth maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: theme.palette.background.card } }}>
        <DialogTitle><MDTypography variant="h5" color={textColor}>Review {review?.title}</MDTypography></DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">Send to {review?.name} / Device #{review?.payload.device_id}</MDTypography>
          {review?.rows.map(row => <MDBox key={row.label} mt={2}>
            <MDTypography variant="caption">{row.label}</MDTypography>
            <MDTypography variant="body2">{row.value}</MDTypography>
          </MDBox>)}
          {review?.kind === "alarms" && <Alert severity="warning" sx={{ mt: 2 }}>
            Sensor-local alarms, not Rivio notifications. Evaluated on samples, not continuously.
            This command disables Alarm2. Firmware units/encoding must match the supplied mm contract.
          </Alert>}
          {review?.kind === "interval" && <Alert severity="info" sx={{ mt: 2 }}>Changes normal upload timing, not sampling or notification rules.</Alert>}
          {review?.kind === "generic" && <Alert severity="warning" sx={{ mt: 2 }}>
            {selected?.description || "Confirm this device action with support."} Network changes can interrupt communication; a restart can pause reporting.
          </Alert>}
          {review?.warnings.map((warning, index) => <Alert severity="warning" sx={{ mt: 1 }}>{warning}</Alert>)}
          <FormControlLabel control={<Checkbox checked={ack} disabled={sending} onChange={event => setAck(event.target.checked)} />}
            label={<MDTypography variant="body2">I've checked the sensor and requested values.</MDTypography>} />
          <MDTypography variant="caption" display="block">Accepted does not mean applied. Wait for device confirmation.</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" disabled={sending} onClick={() => setReview(null)}>Back</MDButton>
          <MDButton color="info" variant="contained" disabled={!ack || !canWrite || sending} onClick={confirmSend}>
            {sending ? "Sending..." : "Send request"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function describeValues(command, values, geometry) {
  const kind = commandKind(command);
  if (kind === "interval") return [{
    label: "Normal reporting interval",
    value: `${values.value / 60} minutes / ${values.value / 3600} hours (${values.value} seconds sent)`,
  }];
  if (kind === "alarms") return ["min1", "max1"].map(key => {
    const height = geometry ? geometry.sensor - values[key] / MM_PER_INCH : null;
    return {
      label: key === "min1" ? "Tank getting full" : "Tank getting empty",
      value: `${height === null ? "" : `${(height / geometry.full * 100).toFixed(2)}% full / ${height.toFixed(2)} in liquid. `}Air gap ${key === "min1" ? "below" : "above"} ${values[key]} mm.`,
    };
  });
  if (!command.fields.length) return [{ label: "Action", value: command.command_name }];
  return command.fields.filter(field => values[field.key] !== undefined).map(field => ({
    label: field.label,
    value: field.type === "select" ? field.options.find(option => option.value === values[field.key])?.label : String(values[field.key]),
  }));
}
